import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import VideoTimecodeEditor from "./VideoTimecodeEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GameForm = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    published: false,
    points_required: 0,
    min_participants: 0,
    registration_deadline: "",
    fallback_to_free: false,
    visibility_delay_minutes: 0,
    game_status: "draft",
    game_mode: "location_objects",
    published_at: ""
  });
  const [hasVideo, setHasVideo] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  
  // Prize states
  const [prizes, setPrizes] = useState({
    first_image: null, first_video: null,
    second_image: null, second_video: null,
    third_image: null, third_video: null
  });

  const isEditMode = !!gameId;

  useEffect(() => {
    if (isEditMode) {
      fetchGame();
    }
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setFormData({
        name: response.data.name,
        description: response.data.description || "",
        published: response.data.published || false,
        points_required: response.data.points_required || 0,
        min_participants: response.data.min_participants || 0,
        registration_deadline: response.data.registration_deadline || "",
        fallback_to_free: response.data.fallback_to_free || false,
        visibility_delay_minutes: response.data.visibility_delay_minutes || 0,
        game_status: response.data.game_status || "draft",
        game_mode: response.data.game_mode || "location_objects",
        published_at: response.data.published_at ? response.data.published_at.slice(0, 16) : ""
      });
      setHasVideo(!!response.data.video_path);
    } catch (error) {
      console.error("Error fetching game:", error);
      toast.error("Ошибка загрузки игры");
      navigate('/');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('video/')) {
        toast.error('Пожалуйста, выберите видео файл');
        return;
      }
      setVideoFile(file);
    }
  };

  const handleVideoUpload = async () => {
    if (!videoFile || !isEditMode) return;
    
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      
      await axios.post(`${API}/games/${gameId}/video`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Видео загружено успешно');
      setHasVideo(true);
      setVideoFile(null);
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки видео');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleVideoDelete = async () => {
    if (!isEditMode) return;
    
    if (!window.confirm('Вы уверены, что хотите удалить видео?')) return;
    
    setUploadingVideo(true);
    try {
      await axios.delete(`${API}/games/${gameId}/video`, {
        headers: getAuthHeader()
      });
      
      toast.success('Видео удалено');
      setHasVideo(false);
      setVideoFile(null);
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Ошибка удаления видео');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Введите название игры");
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await axios.put(`${API}/games/${gameId}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Игра обновлена");
        navigate(`/${gameId}`);
      } else {
        const response = await axios.post(`${API}/games`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Игра создана");
        navigate(`/${response.data.id}`);
      }
    } catch (error) {
      console.error("Error saving game:", error);
      toast.error(error.response?.data?.detail || "Ошибка сохранения игры");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>{isEditMode ? 'Редактировать игру' : 'Создать игру'}</h1>
        <Button variant="outline" onClick={() => navigate(isEditMode ? `/games/${gameId}` : '/')}>
          <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
          Назад
        </Button>
      </div>

      <div className="profile-card">
        <form onSubmit={handleSubmit}>
          <div className="profile-info">
            <div className="profile-field">
              <label htmlFor="name">Название игры *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                placeholder="Введите название игры"
                style={{ 
                  padding: '0.75rem', 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
                required
              />
            </div>

            <div className="profile-field">
              <label htmlFor="description">Описание</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
                placeholder="Опишите вашу игру (необязательно)"
                rows={4}
                style={{ 
                  padding: '0.75rem', 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div className="profile-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="published"
                  checked={formData.published}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Опубликовать игру (пользователи смогут её видеть и играть)</span>
              </label>
            </div>

            {/* Publication date - only for objects_only and location_objects */}
            {(formData.game_mode === "objects_only" || formData.game_mode === "location_objects") && (
              <div className="profile-field" style={{ marginTop: '1rem' }}>
                <label htmlFor="published_at">Дата публикации</label>
                <input
                  id="published_at"
                  name="published_at"
                  type="datetime-local"
                  value={formData.published_at}
                  onChange={handleChange}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '1rem',
                    fontFamily: 'inherit'
                  }}
                />
                <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                  Оставьте пустым для автоматической установки при публикации
                </small>
              </div>
            )}

            {/* Registration Settings Section */}
            <div style={{ 
              marginTop: '2rem', 
              padding: '1.5rem', 
              background: '#fffbeb', 
              borderRadius: '12px',
              border: '1px solid #f59e0b'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                ⭐ Настройки игры за баллы
              </h3>
              
              <div className="profile-field" style={{ marginBottom: '1rem' }}>
                <label htmlFor="points_required">Стоимость участия (баллов)</label>
                <input
                  id="points_required"
                  name="points_required"
                  type="number"
                  min="0"
                  value={formData.points_required}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="0 = бесплатная игра"
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem',
                    width: '200px'
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  0 = бесплатная игра, игроки могут играть сразу
                </p>
              </div>

              <div className="profile-field" style={{ marginBottom: '1rem' }}>
                <label htmlFor="min_participants">Минимум участников для старта</label>
                <input
                  id="min_participants"
                  name="min_participants"
                  type="number"
                  min="0"
                  value={formData.min_participants}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="0 = без ограничений"
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem',
                    width: '200px'
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  0 = игра активируется сразу после первой регистрации
                </p>
              </div>

              <div className="profile-field" style={{ marginBottom: '1rem' }}>
                <label htmlFor="registration_deadline">Крайний срок регистрации</label>
                <input
                  id="registration_deadline"
                  name="registration_deadline"
                  type="datetime-local"
                  value={(() => {
                    if (!formData.registration_deadline) return '';
                    try {
                      const date = new Date(formData.registration_deadline);
                      // Format: YYYY-MM-DDTHH:MM (local time)
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    } catch {
                      return '';
                    }
                  })()}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Convert local datetime to ISO string
                      const localDate = new Date(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        registration_deadline: localDate.toISOString()
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        registration_deadline: ''
                      }));
                    }
                  }}
                  disabled={loading}
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Если не набралось минимум участников — баллы вернутся игрокам
                </p>
              </div>

              <div className="profile-field" style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  backgroundColor: formData.fallback_to_free ? '#ecfdf5' : '#f9fafb',
                  borderRadius: '8px',
                  border: formData.fallback_to_free ? '2px solid #10b981' : '2px solid #e5e7eb',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    id="fallback_to_free"
                    name="fallback_to_free"
                    type="checkbox"
                    checked={formData.fallback_to_free}
                    onChange={handleChange}
                    disabled={loading}
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <div>
                    <span style={{ fontWeight: 600, color: '#1f2937' }}>
                      Опубликовать как бесплатную при недоборе
                    </span>
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      Если участников меньше минимума — игра станет бесплатной вместо отмены. Баллы вернутся зарегистрированным игрокам.
                    </p>
                  </div>
                </label>
              </div>

              <div className="profile-field" style={{ marginBottom: '1rem' }}>
                <label htmlFor="visibility_delay_minutes">Задержка появления на главной (минуты)</label>
                <input
                  id="visibility_delay_minutes"
                  name="visibility_delay_minutes"
                  type="number"
                  min="0"
                  max="1440"
                  value={formData.visibility_delay_minutes}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem',
                    width: '150px'
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  После публикации игра будет доступна по прямой ссылке сразу, но на главной странице появится через указанное время. 0 = сразу.
                </p>
              </div>

              <div className="profile-field">
                <label htmlFor="game_status">Статус игры</label>
                <select
                  id="game_status"
                  name="game_status"
                  value={formData.game_status}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem',
                    width: '250px'
                  }}
                >
                  <option value="draft">Черновик</option>
                  <option value="collecting">Сбор заявок</option>
                  <option value="active">Активна (можно играть)</option>
                  <option value="cancelled">Отменена</option>
                  <option value="completed">Завершена</option>
                </select>
              </div>

              <div className="profile-field">
                <label htmlFor="game_mode">Режим игры</label>
                <select
                  id="game_mode"
                  name="game_mode"
                  value={formData.game_mode}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem',
                    width: '250px',
                    background: formData.game_mode === 'objects_only' ? '#fef3c7' : '#ecfdf5',
                  }}
                >
                  <option value="location_objects">📍 Место + объекты</option>
                  <option value="objects_only">🎯 Только объекты</option>
                </select>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  {formData.game_mode === 'objects_only' 
                    ? 'Игроки ищут только объекты на панорамах' 
                    : 'Игроки указывают место съемки и ищут объекты'}
                </p>
              </div>
            </div>

            {isEditMode && (
              <div className="profile-field" style={{ marginTop: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Видео для игры
                </label>
                
                {hasVideo ? (
                  <div>
                    <div style={{ 
                      padding: '1rem', 
                      background: '#f0fdf4', 
                      border: '2px solid #86efac',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ color: '#16a34a', fontWeight: 500 }}>
                        ✓ Видео загружено
                      </span>
                      <button
                        type="button"
                        onClick={handleVideoDelete}
                        disabled={uploadingVideo}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: uploadingVideo ? 'not-allowed' : 'pointer',
                          opacity: uploadingVideo ? 0.5 : 1
                        }}
                      >
                        {uploadingVideo ? 'Удаление...' : 'Удалить видео'}
                      </button>
                    </div>
                    <video
                      controls
                      style={{
                        width: '100%',
                        maxHeight: '400px',
                        borderRadius: '8px',
                        border: '2px solid #e2e8f0'
                      }}
                      src={`${API}/games/${gameId}/video`}
                    >
                      Ваш браузер не поддерживает видео.
                    </video>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoChange}
                      disabled={uploadingVideo}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    {videoFile && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                          Выбран файл: {videoFile.name}
                        </p>
                        <button
                          type="button"
                          onClick={handleVideoUpload}
                          disabled={uploadingVideo}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: uploadingVideo ? 'not-allowed' : 'pointer',
                            opacity: uploadingVideo ? 0.5 : 1
                          }}
                        >
                          {uploadingVideo ? 'Загрузка...' : 'Загрузить видео'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                  Поддерживаемые форматы: MP4, WebM, OGG, MOV
                </p>
                
                {/* Video Timecode Editor - show only when video is loaded */}
                {hasVideo && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <VideoTimecodeEditor gameId={gameId} />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="profile-actions">
            <Button type="submit" disabled={loading}>
              <Save size={18} style={{ marginRight: '0.5rem' }} />
              {loading ? 'Сохранение...' : (isEditMode ? 'Сохранить' : 'Создать')}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(isEditMode ? `/games/${gameId}` : '/')}
              disabled={loading}
            >
              Отмена
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GameForm;
